import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const CompCombinaciones = () => {
  const { token } = useAuth();
  const [productos, setProductos] = useState([]);
  const [combinaciones, setCombinaciones] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filtroCombinacion, setFiltroCombinacion] = useState(null);

  // Cargar productos y combinaciones
  useEffect(() => {
    const fetchData = async () => {
      const prods = await axios.get(`${import.meta.env.VITE_API_URL}/productos/admin`, {
        headers: { "x-auth-token": token },
      });
      setProductos(prods.data.productos);

      const combs = await axios.get(`${import.meta.env.VITE_API_URL}/combinaciones`, {
        headers: { "x-auth-token": token },
      });
      setCombinaciones(combs.data);
    };
    if (token) fetchData();
  }, [token]);

  // Selección de productos
  const toggleProducto = (id) => {
    const prod = productos.find((p) => p.id === id);
    if (!prod) return;

    // Si es el primero, setea el filtro
    if (selectedIds.length === 0) {
      setFiltroCombinacion({
        cantidad_bultos: prod.cantidad_bultos,
        precio_mayorista_a: prod.precios?.[0]?.precio_mayorista_a,
        nombre: prod.nombre_producto.split(" ")[0]?.toLowerCase() || "",
      });
      setSelectedIds([id]);
      return;
    }

    // Si ya está seleccionado, lo deselecciona y resetea filtro si queda vacío
    if (selectedIds.includes(id)) {
      const nuevos = selectedIds.filter((pid) => pid !== id);
      setSelectedIds(nuevos);
      if (nuevos.length === 0) setFiltroCombinacion(null);
      return;
    }

    // Aplica filtro
    if (
      prod.cantidad_bultos === filtroCombinacion.cantidad_bultos &&
      prod.precios?.[0]?.precio_mayorista_a === filtroCombinacion.precio_mayorista_a &&
      prod.nombre_producto.toLowerCase().includes(filtroCombinacion.nombre)
    ) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Crear combinación
  const handleCrear = async () => {
    setError("");
    setSuccess("");
    if (selectedIds.length < 2) {
      setError("Selecciona al menos dos productos.");
      return;
    }
    if (!nombre.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    try {
      const res = await axios.post(
        `${import.meta.env.VITE_API_URL}/combinaciones`,
        { nombre, productos_ids: selectedIds },
        { headers: { "x-auth-token": token } }
      );
      setSuccess("¡Combinación creada!");
      setCombinaciones((prev) => [...prev, res.data.combinacion]);
      setSelectedIds([]);
      setNombre("");
    } catch (err) {
      setError(err.response?.data?.error || "Error al crear combinación");
    }
  };

  // Eliminar combinación
  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar combinación?")) return;
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/combinaciones/${id}`, {
        headers: { "x-auth-token": token },
      });
      setCombinaciones((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError("Error al eliminar combinación");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Crear Combinación</h2>
      <input
        className="border p-2 rounded mb-2 w-full"
        placeholder="Nombre de la combinación"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
      />
      <div className="mb-2 text-sm text-gray-600">Selecciona productos:</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        {productos.map((prod) => {
          // Si hay filtro, solo muestra los que cumplen la condición o ya están seleccionados
          const cumpleFiltro =
            !filtroCombinacion ||
            selectedIds.includes(prod.id) ||
            (
              prod.cantidad_bultos === filtroCombinacion.cantidad_bultos &&
              prod.precios?.[0]?.precio_mayorista_a === filtroCombinacion.precio_mayorista_a &&
              prod.nombre_producto.toLowerCase().includes(filtroCombinacion.nombre)
            );
          if (!cumpleFiltro) return null;
          return (
            <label
              key={prod.id}
              className={`border rounded p-2 cursor-pointer flex items-center gap-2 ${
                selectedIds.includes(prod.id) ? "bg-green-100 border-green-400" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(prod.id)}
                onChange={() => toggleProducto(prod.id)}
              />
              <span>{prod.nombre_producto}</span>
            </label>
          );
        })}
      </div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      {success && <div className="text-green-600 mb-2">{success}</div>}
      <button
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        onClick={handleCrear}
      >
        Crear combinación
      </button>

      <h3 className="text-xl font-bold mt-8 mb-2">Combinaciones existentes</h3>
      <ul className="space-y-2">
        {combinaciones.map((comb) => (
          <li key={comb.id} className="border rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <span className="font-semibold">{comb.nombre}</span>
              <span className="ml-2 text-gray-500 text-xs">
                ({comb.productos?.map((p) => p.nombre_producto).join(", ")})
              </span>
            </div>
            <button
              className="text-red-600 hover:underline mt-2 md:mt-0"
              onClick={() => handleDelete(comb.id)}
            >
              Eliminar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CompCombinaciones;
